import { ref, onBeforeMount, onMounted, computed, watch, unref, watchEffect, inject, provide } from 'vue';
import { isString, noop } from '@chakra-ui/utils';
import { tryOnScopeDispose, defaultWindow } from '@chakra-ui/vue-utils';

/**
 * Credit: https://github.com/reach/reach-ui/blob/develop/packages/auto-id/src/index.tsx
 *
 * Why does this hook exist?
 *   1. Accessibiliy APIs rely heavily on element IDs
 *   2. Requiring developers to put IDs on every Chakra component
 *      is cumbersome and error-prone.
 *   3. With a components model, we can generate IDs for them!
 *
 * Solutions to ID problem:
 * 1. Generate random IDs
 *    In v0.x of @chakra-ui/vue, we did this for components.
 *    Since then, we've learned some things about performance for
 *    components especially with SSR.
 *
 *    This may not be a good idea because during server rendering
 *    the IDs will be statically generated, and during client-side hydration,
 *    the IDs may not match, when booting up the Vue App. Vue will then
 *    go ahead and recreate the entire application.
 *
 * 2. Don't server render IDs. Instead patch on client `onMounted`
 *    In this approach, generated ID is an empty string on the first render.
 *    This way the client and server possess the same ID.
 *
 *    When the component is finally mounted, we patch the ID.
 *    This may cause a re-render on the client, but it shouldn't be a
 *    big problem, because:
 *
 *        1. Components using `useId` composable are small
 *        2. With solution 1, it would cause a re-render anyway.
 *        3. This patch only runs once. (Only when the `onMounted` life
 *           -cycle hook is called.)
 *
 */
var serverHandoffComplete = false;
var _id = 0;

var genId = function genId() {
  return ++_id;
};
/**
 * Generates a unique id
 *
 * @param id external ID provided by consumer/user.
 * @param prefix prefix to append before the id
 */


var useId = function useId(id, prefix) {
  var initialId = id || (serverHandoffComplete ? genId() : null);
  var uid = ref(initialId);
  onBeforeMount(function () {
    if (serverHandoffComplete === false) {
      serverHandoffComplete = true;
    }
  });
  onMounted(function () {
    if (uid.value === null) {
      uid.value = genId();
    }
  });
  return computed(function () {
    var __id__ = uid.value !== null ? uid.value.toString() : undefined;

    return prefix ? prefix + "-" + __id__ : __id__;
  });
};
/**
 * Hook to generate ids for use in compound components
 *
 * @param id the external id passed from the user
 * @param prefixes array of prefixes to use
 */

function useIds(id) {
  var __id__ = useId(id);

  for (var _len = arguments.length, prefixes = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    prefixes[_key - 1] = arguments[_key];
  }

  return prefixes.map(function (prefix) {
    return computed(function () {
      return prefix + "-" + __id__.value;
    });
  });
}

/**
 * Much of this has ben adopted from the cgood folks at @vueuse/core
 */
function useEventListener() {
  var target;
  var event;
  var listener;
  var options;

  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  if (isString(args[0])) {
    event = args[0];
    listener = args[1];
    options = args[2];
    target = defaultWindow;
  } else {
    target = args[0];
    event = args[1];
    listener = args[2];
    options = args[3];
  }

  if (!target) return noop;
  var _cleanup = noop;
  var stopWatch = watch(function () {
    return unref(target);
  }, function (el) {
    _cleanup();

    if (!el) return;
    el.addEventListener(event, listener, options);

    _cleanup = function cleanup() {
      el.removeEventListener(event, listener, options);
      _cleanup = noop;
    };
  }, {
    immediate: true,
    flush: "post"
  });

  var stop = function stop() {
    stopWatch();

    _cleanup();
  };

  tryOnScopeDispose(stop);
  return stop;
}

function useWindowEvent(type, listener, options) {
  if (typeof window === "undefined") return;
  watchEffect(function (onInvalidate) {
    window.addEventListener(type, listener, options);
    onInvalidate(function () {
      window.removeEventListener(type, listener, options);
    });
  });
}

var StackContext = Symbol("ChakraElementStackContext");
var StackMessage;

(function (StackMessage) {
  StackMessage[StackMessage["AddElement"] = 0] = "AddElement";
  StackMessage[StackMessage["RemoveElement"] = 1] = "RemoveElement";
})(StackMessage || (StackMessage = {}));

function useStackContext() {
  return inject(StackContext, function () {});
}
function useElementStack(element) {
  var notify = useStackContext();
  watchEffect(function (onInvalidate) {
    var domElement = element == null ? void 0 : element.value;
    if (!domElement) return;
    notify(StackMessage.AddElement, domElement);
    onInvalidate(function () {
      return notify(StackMessage.RemoveElement, domElement);
    });
  });
}
function useStackProvider(onUpdate) {
  var parentUpdate = useStackContext();

  function notify() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    // Notify our layer
    onUpdate == null ? void 0 : onUpdate.apply(void 0, args); // Notify the parent

    parentUpdate.apply(void 0, args);
  }

  provide(StackContext, notify);
}

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }

  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}

function _asyncToGenerator(fn) {
  return function () {
    var self = this,
        args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);

      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }

      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }

      _next(undefined);
    });
  };
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _createForOfIteratorHelperLoose(o, allowArrayLike) {
  var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];
  if (it) return (it = it.call(o)).next.bind(it);

  if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") {
    if (it) o = it;
    var i = 0;
    return function () {
      if (i >= o.length) return {
        done: true
      };
      return {
        done: false,
        value: o[i++]
      };
    };
  }

  throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function useClipboard(_ref) {
  var source = _ref.source,
      _ref$copyTimeout = _ref.copyTimeout,
      copyTimeout = _ref$copyTimeout === void 0 ? 1000 : _ref$copyTimeout,
      _ref$read = _ref.read,
      read = _ref$read === void 0 ? false : _ref$read;
  var isSupported = Boolean(navigator && "clipboard" in navigator);
  var text = ref("");
  var copied = ref(false);

  var timeout = function timeout() {
    setTimeout(function () {
      copied.value = false;
    }, copyTimeout);
  };

  var events = ["copy", "cut"];

  if (isSupported && read) {
    for (var _iterator = _createForOfIteratorHelperLoose(events), _step; !(_step = _iterator()).done;) {
      var event = _step.value;
      useEventListener(event, updateText);
    }
  }

  function updateText() {
    navigator.clipboard.readText().then(function (value) {
      text.value = value;
    });
  }

  function copy(_x) {
    return _copy.apply(this, arguments);
  }

  function _copy() {
    _copy = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee(_value) {
      var value;
      return regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              if (_value === void 0) {
                _value = unref(source);
              }

              value = unref(_value);

              if (!(isSupported && value != null)) {
                _context.next = 8;
                break;
              }

              _context.next = 5;
              return navigator.clipboard.writeText(value);

            case 5:
              text.value = value;
              copied.value = true;
              timeout();

            case 8:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));
    return _copy.apply(this, arguments);
  }

  return {
    copy: copy,
    text: text,
    copied: copied
  };
}

export { StackMessage, useClipboard, useElementStack, useEventListener, useId, useIds, useStackContext, useStackProvider, useWindowEvent };
